/**
 * Supported locales — covers ~97% of world internet users.
 * Country → locale mapping is handled in middleware.ts.
 */
export const locales = [
  'en',   // English (default)
  'es',   // Spanish
  'fr',   // French
  'de',   // German
  'pt',   // Portuguese
  'it',   // Italian
  'nl',   // Dutch
  'sv',   // Swedish
  'da',   // Danish
  'no',   // Norwegian
  'fi',   // Finnish
  'pl',   // Polish
  'ru',   // Russian
  'uk',   // Ukrainian
  'cs',   // Czech
  'ro',   // Romanian
  'hu',   // Hungarian
  'sk',   // Slovak
  'hr',   // Croatian
  'sr',   // Serbian
  'bg',   // Bulgarian
  'sl',   // Slovenian
  'lt',   // Lithuanian
  'lv',   // Latvian
  'et',   // Estonian
  'el',   // Greek
  'tr',   // Turkish
  'ar',   // Arabic
  'he',   // Hebrew
  'fa',   // Persian / Farsi
  'hi',   // Hindi
  'bn',   // Bengali
  'ur',   // Urdu
  'ta',   // Tamil
  'te',   // Telugu
  'mr',   // Marathi
  'gu',   // Gujarati
  'pa',   // Punjabi
  'ml',   // Malayalam
  'th',   // Thai
  'vi',   // Vietnamese
  'id',   // Indonesian
  'ms',   // Malay
  'tl',   // Filipino / Tagalog
  'ko',   // Korean
  'ja',   // Japanese
  'zh',   // Chinese (Simplified)
  'zh-TW', // Chinese (Traditional)
  'my',   // Burmese
  'km',   // Khmer
  'sw',   // Swahili
  'am',   // Amharic
  'ca',   // Catalan
  // African languages
  'ha',   // Hausa — Nigeria, Niger, Ghana, Cameroon
  'yo',   // Yoruba — Nigeria, Benin, Togo
  'ig',   // Igbo — Nigeria
  'zu',   // Zulu — South Africa
  'xh',   // Xhosa — South Africa
  'af',   // Afrikaans — South Africa, Namibia
  'so',   // Somali — Somalia, Ethiopia, Kenya, Djibouti
  'om',   // Oromo — Ethiopia
  'wo',   // Wolof — Senegal, Gambia
  'sn',   // Shona — Zimbabwe
  'ny',   // Chichewa/Nyanja — Malawi, Zambia, Mozambique
  'ln',   // Lingala — DRC, Republic of Congo
  'rw',   // Kinyarwanda — Rwanda
  'lg',   // Luganda — Uganda
  'ti',   // Tigrinya — Ethiopia, Eritrea
  'tn',   // Tswana — Botswana, South Africa
  'st',   // Sesotho — Lesotho, South Africa
  'mg',   // Malagasy — Madagascar
  'sg',   // Sango — Central African Republic
  'ak',   // Twi/Akan — Ghana
] as const;

export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = 'en';

/**
 * Maps ISO 3166-1 alpha-2 country codes to locale strings.
 * Used by middleware to auto-detect language from Vercel geo IP.
 */
export const countryToLocale: Record<string, Locale> = {
  // English
  US: 'en', GB: 'en', AU: 'en', CA: 'en', NZ: 'en', IE: 'en', ZA: 'en',
  JM: 'en', TT: 'en', BB: 'en', KE: 'en',
  ZM: 'en', TZ: 'en',
  // African language overrides
  NG: 'yo',  // Nigeria — Yoruba (largest group; Hausa = 'ha')
  GH: 'ak',  // Ghana — Twi/Akan
  UG: 'lg',  // Uganda — Luganda
  ZW: 'sn',  // Zimbabwe — Shona
  RW: 'rw',  // Rwanda — Kinyarwanda
  BW: 'tn',  // Botswana — Tswana
  NA: 'af',  // Namibia — Afrikaans
  MW: 'ny',  // Malawi — Chichewa
  SO: 'so',  // Somalia — Somali
  MG: 'mg',  // Madagascar — Malagasy
  CF: 'sg',  // Central African Republic — Sango
  CD: 'ln',  // DRC — Lingala
  CG: 'ln',  // Republic of Congo — Lingala
  ER: 'ti',  // Eritrea — Tigrinya
  LS: 'st',  // Lesotho — Sesotho
  GM: 'wo',  // Gambia — Wolof
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
  EC: 'es', BO: 'es', PY: 'es', UY: 'es', CR: 'es', PA: 'es', DO: 'es',
  GT: 'es', HN: 'es', SV: 'es', NI: 'es', CU: 'es', PR: 'es',
  // French
  FR: 'fr', BE: 'fr', CH: 'fr', LU: 'fr', MC: 'fr', SN: 'fr', CI: 'fr',
  CM: 'fr', BF: 'fr', ML: 'fr', GN: 'fr', NE: 'fr',
  TG: 'fr', BJ: 'fr', GA: 'fr', TD: 'fr', DZ: 'fr',
  TN: 'fr', MA: 'fr', MR: 'fr', HT: 'fr',
  // CD, CG, MG, CF handled above with African language overrides
  // German
  DE: 'de', AT: 'de', LI: 'de',
  // Portuguese
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt', ST: 'pt', GW: 'pt',
  // Italian
  IT: 'it', SM: 'it', VA: 'it',
  // Dutch
  NL: 'nl', SR: 'nl', AW: 'nl', CW: 'nl',
  // Swedish
  SE: 'sv',
  // Danish
  DK: 'da', GL: 'da', FO: 'da',
  // Norwegian
  NO: 'no',
  // Finnish
  FI: 'fi',
  // Polish
  PL: 'pl',
  // Russian
  RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru',
  // Ukrainian
  UA: 'uk',
  // Czech
  CZ: 'cs',
  // Romanian
  RO: 'ro', MD: 'ro',
  // Hungarian
  HU: 'hu',
  // Slovak
  SK: 'sk',
  // Croatian
  HR: 'hr',
  // Serbian
  RS: 'sr', BA: 'sr', ME: 'sr',
  // Bulgarian
  BG: 'bg',
  // Slovenian
  SI: 'sl',
  // Lithuanian
  LT: 'lt',
  // Latvian
  LV: 'lv',
  // Estonian
  EE: 'et',
  // Greek
  GR: 'el', CY: 'el',
  // Turkish
  TR: 'tr', AZ: 'tr',
  // Arabic
  SA: 'ar', EG: 'ar', AE: 'ar', IQ: 'ar', JO: 'ar', LB: 'ar', SY: 'ar',
  YE: 'ar', KW: 'ar', QA: 'ar', BH: 'ar', OM: 'ar', LY: 'ar', SD: 'ar',
  DJ: 'ar', KM: 'ar', PS: 'ar',
  // SO is handled above as 'so' (Somali)
  // Hebrew
  IL: 'he',
  // Persian
  IR: 'fa', AF: 'fa',
  // Hindi
  IN: 'hi',
  // Bengali
  BD: 'bn',
  // Urdu
  PK: 'ur',
  // Thai
  TH: 'th',
  // Vietnamese
  VN: 'vi',
  // Indonesian
  ID: 'id',
  // Malay
  MY: 'ms', BN: 'ms',
  // Filipino
  PH: 'tl',
  // Korean
  KR: 'ko',
  // Japanese
  JP: 'ja',
  // Chinese Simplified
  CN: 'zh', SG: 'zh',
  // Chinese Traditional
  TW: 'zh-TW', HK: 'zh-TW', MO: 'zh-TW',
  // Burmese
  MM: 'my',
  // Khmer
  KH: 'km',
  // Swahili
  KE_sw: 'sw', // Kenya has both EN and SW — handled specially in middleware
  // Amharic
  ET: 'am',
  // Catalan — no dedicated country, handled via Accept-Language
  // French overseas departments and territories
  RE: 'fr', GP: 'fr', MQ: 'fr', GF: 'fr', YT: 'fr', PM: 'fr', NC: 'fr', PF: 'fr', WF: 'fr', BL: 'fr', MF: 'fr',
};

/**
 * Countries with more than one everyday language: here the visitor's own
 * browser language wins when it is one of them (a French-speaking Canadian or
 * Belgian gets French, a Flemish visitor Dutch); otherwise the country's
 * default above. Everywhere else the country decides (Gordon 2026-11-02:
 * "auto-select language depending on what country you are surfing from").
 */
export const countryLanguages: Record<string, Locale[]> = {
  CA: ['en', 'fr'], BE: ['fr', 'nl', 'de'], CH: ['de', 'fr', 'it'], LU: ['fr', 'de'],
  CM: ['fr', 'en'], MA: ['fr', 'ar'], DZ: ['fr', 'ar'], TN: ['fr', 'ar'], MR: ['fr', 'ar'],
  FI: ['fi', 'sv'], ES: ['es', 'ca'], IE: ['en'], ZA: ['en', 'af', 'zu', 'xh', 'st', 'tn'],
  KE: ['en', 'sw'], TZ: ['sw', 'en'], NG: ['yo', 'en', 'ha', 'ig'], IN: ['hi', 'en', 'bn', 'ta', 'te', 'mr', 'gu', 'pa', 'ml', 'ur'],
  PK: ['ur', 'en'], SG: ['en', 'zh', 'ms', 'ta'], MY: ['ms', 'en', 'zh', 'ta'], PH: ['tl', 'en'], IL: ['he', 'ar', 'en'],
  US: ['en', 'es'], RW: ['rw', 'fr', 'en'], CD: ['ln', 'fr'], CG: ['ln', 'fr'], MG: ['mg', 'fr'], HT: ['fr'],
};
